import { oldData } from "@/app/[lng]/(main)/product/[...country]/data"
import { localizePathname } from "@/lib/locale-routing"

function normalizeQueryString(queryString?: string): string {
  if (!queryString) return ""
  if (queryString.startsWith("?")) return queryString.slice(1)
  return queryString.replace(/^&+/, "")
}

function dedupeIso2sPreserveOrder(iso2s: string[]): string[] {
  const seen = new Set<string>()
  const result: string[] = []

  for (const iso2 of iso2s) {
    const normalized = iso2.toUpperCase()
    if (seen.has(normalized)) continue
    seen.add(normalized)
    result.push(normalized)
  }

  return result
}

export function normalizeIso2s(iso2s: string[]): string[] {
  return Array.from(new Set(iso2s.map((iso2) => iso2.toUpperCase()))).sort()
}

export function resolveSingleCountrySlug(iso2: string): string | null {
  const targetIso2 = iso2.toUpperCase()
  let bestMatch: { slug: string; nameLength: number } | null = null

  for (const [slug, data] of Object.entries(oldData)) {
    if (data.iso2s.length !== 1 || data.iso2s[0].toUpperCase() !== targetIso2) {
      continue
    }

    const candidate = { slug, nameLength: data.name.length }
    if (!bestMatch || candidate.nameLength < bestMatch.nameLength) {
      bestMatch = candidate
    }
  }

  return bestMatch?.slug || null
}

export function resolveBestMultiCountrySlug(
  targetIso2s: string[],
  requiredIso2s: string[] = []
): string | null {
  const normalizedTarget = normalizeIso2s(targetIso2s)
  const requiredSet = new Set(normalizeIso2s(requiredIso2s))
  let bestMatch: { slug: string; score: number } | null = null

  for (const [slug, data] of Object.entries(oldData)) {
    if (data.iso2s.length <= 1) {
      continue
    }

    const candidateIso2s = normalizeIso2s(data.iso2s)
    if (Array.from(requiredSet).some((iso2) => !candidateIso2s.includes(iso2))) {
      continue
    }

    const intersectionCount = normalizedTarget.filter((iso2) => candidateIso2s.includes(iso2)).length
    if (intersectionCount === 0) {
      continue
    }

    const isExactMatch =
      intersectionCount === normalizedTarget.length &&
      candidateIso2s.length === normalizedTarget.length
    const coverageScore = intersectionCount / normalizedTarget.length
    const precisionScore = intersectionCount / candidateIso2s.length
    const score = (isExactMatch ? 1000 : 0) + coverageScore * 100 + precisionScore

    if (!bestMatch || score > bestMatch.score) {
      bestMatch = { slug, score }
    }
  }

  return bestMatch?.slug || null
}

export function resolveProductSlugForIso2s(iso2s: string[]): string | null {
  const normalizedIso2s = normalizeIso2s(iso2s)
  if (normalizedIso2s.length === 0) return null
  if (normalizedIso2s.length === 1) {
    return resolveSingleCountrySlug(normalizedIso2s[0])
  }
  return resolveBestMultiCountrySlug(normalizedIso2s, normalizedIso2s)
}

export function buildProductHref(lng: string, slug: string | null, queryString?: string): string | undefined {
  if (!slug) return undefined
  const normalizedQuery = normalizeQueryString(queryString)
  const lngPrefix = `/${lng}`
  return `${lngPrefix}/product/${slug}${normalizedQuery ? `?${normalizedQuery}` : ""}`
}

export function resolveProductHrefForIso2s(
  lng: string,
  iso2s: string[],
  queryString?: string
): string | undefined {
  return buildProductHref(lng, resolveProductSlugForIso2s(iso2s), queryString)
}

export function buildDefaultProductQueryString(iso2s: string[], queryString?: string): string {
  const params = new URLSearchParams()
  params.set("countries", dedupeIso2sPreserveOrder(iso2s).join(","))
  params.set("day", "7")
  params.set("dataMode", "total")
  params.set("quota", "10240")

  const normalizedQuery = normalizeQueryString(queryString)
  if (normalizedQuery) {
    for (const [key, value] of new URLSearchParams(normalizedQuery)) {
      params.append(key, value)
    }
  }

  return params.toString()
}

export function resolveProductLandingHref(
  lng: string,
  iso2s: string[],
  queryString?: string
): string | undefined {
  return buildProductHref(
    lng,
    resolveProductSlugForIso2s(iso2s),
    buildDefaultProductQueryString(iso2s, queryString)
  )
}

export function buildBuyHref(lng: string, queryString?: string): string {
  const normalizedQuery = normalizeQueryString(queryString)
  const buyHref = localizePathname("/buy", lng)
  return normalizedQuery ? `${buyHref}?${normalizedQuery}` : buyHref
}

export function resolvePreferredProductHref(
  lng: string,
  iso2s: string[],
  queryString?: string
): string {
  const landingHref = resolveProductLandingHref(lng, iso2s, queryString)
  if (landingHref) return landingHref

  const normalizedIso2s = dedupeIso2sPreserveOrder(iso2s)
  const fallbackSlug = normalizedIso2s[0] ? resolveSingleCountrySlug(normalizedIso2s[0]) : null
  const fallbackHref = buildProductHref(
    lng,
    fallbackSlug,
    buildDefaultProductQueryString(normalizedIso2s, queryString)
  )

  if (fallbackHref) return fallbackHref

  return buildBuyHref(lng, queryString)
}
